import { useCallback, useEffect, useRef, useState } from 'react';

/** Slack so a fractional scroll position does not read as "scrolled away". */
const BOTTOM_TOLERANCE = 8;

interface StickToBottom<T extends HTMLElement> {
  ref: React.RefObject<T | null>;
  /** True while the scroller is following new content. */
  isPinned: boolean;
  isAwayFromBottom: boolean;
  scrollToBottom: (behavior?: ScrollBehavior) => void;
}

/**
 * Keeps a scroller pinned to the newest content while the reader is at the
 * bottom, and gets out of the way the moment they scroll up — otherwise a
 * streaming reply drags the viewport away from whatever they were reading.
 */
export function useStickToBottom<T extends HTMLElement>(): StickToBottom<T> {
  const ref = useRef<T>(null);
  const [isPinned, setIsPinned] = useState(true);
  // Read inside the observer callback, which must not be re-subscribed per change.
  const pinned = useRef(true);

  const setPinned = useCallback((next: boolean) => {
    pinned.current = next;
    setIsPinned(next);
  }, []);

  const scrollToBottom = useCallback(
    (behavior: ScrollBehavior = 'auto') => {
      const el = ref.current;
      if (!el) return;
      setPinned(true);
      const top = el.scrollHeight - el.clientHeight;

      // Assigning scrollTop after a smooth scrollTo cancels the animation and
      // lands instantly — which is why "jump to latest" snapped rather than
      // glided. Only fall back to the assignment when there is no animation to
      // cancel, or no scrollTo at all (jsdom).
      if (behavior === 'smooth' && typeof el.scrollTo === 'function') {
        el.scrollTo({ top, behavior });
        return;
      }
      el.scrollTo?.({ top, behavior });
      el.scrollTop = top;
    },
    [setPinned],
  );

  useEffect(() => {
    const el = ref.current;
    if (!el) return;

    const atBottom = () =>
      el.scrollTop + el.clientHeight >= el.scrollHeight - BOTTOM_TOLERANCE;

    const onScroll = () => setPinned(atBottom());

    // A streamed reply changes height without firing a scroll event.
    const follow = () => {
      if (!pinned.current) return;
      el.scrollTop = el.scrollHeight - el.clientHeight;
    };

    el.addEventListener('scroll', onScroll, { passive: true });
    const observer = new ResizeObserver(follow);
    observer.observe(el);
    Array.from(el.children).forEach((child) => observer.observe(child));

    return () => {
      el.removeEventListener('scroll', onScroll);
      observer.disconnect();
    };
  }, [setPinned]);

  return { ref, isPinned, isAwayFromBottom: !isPinned, scrollToBottom };
}
